import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import {
  Button,
  Icon,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  ScrollArea,
  inputVariants,
  Input,
} from "@elaniintc/goes-design-system";
import { cn } from "@/utils/theme";
import type {
  Signer,
  SignersSelectedType,
  SignerType,
  WorkPosition,
} from "../types";
import { mockWorkPositions } from "../types";
import { useGetUsers } from "@/queries/users/users.api";
import { transformUserToSigner } from "../serialization";

/**
 * Componente SignersList - Manejo de firmantes y suplentes
 *
 * CAMBIOS IMPLEMENTADOS:
 * - Ahora obtiene usuarios reales del sistema via API (useGetUsers)
 * - Las posiciones de trabajo se generan dinámicamente basadas en los usuarios del API
 * - Los mock de empleados han sido reemplazados por usuarios del sistema
 * - Se mantiene la estructura de posiciones de trabajo para la UI
 * - El valor de los firmantes será el ID real del usuario del sistema
 */

// Función para generar iniciales del nombre
const getInitials = (name?: string): string | null => {
  if (!name) return null;

  return name
    .split(" ")
    .map((word) => word.charAt(0))
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

interface SignerSection {
  id: string;
  primarySigner?: Signer | null;
  backupSigner?: Signer | null;
}

interface SignersListProps {
  isLoadingSigners: boolean;
  signersError: string | null;
  selectedSigners: SignersSelectedType;
  onAddSigner: (
    signer: Signer | undefined,
    type: SignerType,
    index: number,
  ) => void;
  onRemoveSigner: (id?: string) => void;
  onUpdateSigner: (index: number, signer: Signer, type: SignerType) => void;
  setSelectedSigners: React.Dispatch<React.SetStateAction<SignersSelectedType>>;
}

export const SignersList: React.FC<SignersListProps> = ({
  selectedSigners,
  isLoadingSigners,
  signersError,
  onAddSigner,
  onRemoveSigner,
  onUpdateSigner,
  setSelectedSigners,
}) => {
  const [numberOfSections, setNumberOfSections] = useState(1);

  // Obtener usuarios del API
  const {
    data: usersData,
    isLoading: isLoadingUsers,
    error: usersError,
  } = useGetUsers({
    per_page: 10000, // Obtener todos los usuarios disponibles
    status: "Activo", // Solo usuarios activos
  });

  // Transformar usuarios del API a formato Signer
  const apiUsers = useMemo(() => {
    if (!usersData?.data) return [];
    return usersData.data.map(transformUserToSigner);
  }, [usersData]);

  const isStringPairArray = useCallback(
    (arr: SignerPair[]) =>
      arr.every(
        (s) =>
          (typeof s.primary === "string" && typeof s.backup === "string") ||
          s.backup === null ||
          s.primary === null,
      ),
    [],
  );

  // guarda si la última vez eran strings
  const lastWasStringsRef = useRef(false);

  useEffect(() => {
    if (!usersData?.data?.length) return;

    const pairs = selectedSigners as SignerPair[];
    const areStringsNow = isStringPairArray(pairs);

    if (!areStringsNow || lastWasStringsRef.current) {
      lastWasStringsRef.current = areStringsNow;
      return;
    }

    const usersById = Object.fromEntries(usersData.data.map((u) => [u.id, u]));

    const hydrated = pairs.map((s) => {
      const primaryUser =
        typeof s.primary === "string" ? usersById[s.primary] : null;
      const backupUser =
        typeof s.backup === "string" ? usersById[s.backup] : null;

      return {
        primary: primaryUser ? transformUserToSigner(primaryUser) : null,
        backup: backupUser ? transformUserToSigner(backupUser) : null,
      };
    });

    // ✅ evita set si ya está igual (extra seguro)
    const same = JSON.stringify(hydrated) === JSON.stringify(selectedSigners);
    if (!same) {
      setSelectedSigners(hydrated);
    }

    // marca que ya hidrataste este lote de strings
    lastWasStringsRef.current = true;
  }, [usersData, selectedSigners, setSelectedSigners, isStringPairArray]);

  useEffect(() => {
    const pairsCount = Array.isArray(selectedSigners)
      ? selectedSigners.length
      : 0;
    const desired = Math.max(1, pairsCount); // al menos 1 sección

    setNumberOfSections((prev) => (prev === desired ? prev : desired));
  }, [selectedSigners]);

  // Crear posiciones de trabajo dinámicamente basadas en los usuarios del API
  const workPositionsWithApiUsers = useMemo(() => {
    if (!apiUsers.length) return mockWorkPositions;

    // Crear un mapa de posiciones basado en los usuarios del API
    const positionsMap = new Map<
      string,
      { id: string; name: string; users: Signer[] }
    >();

    apiUsers.forEach((user) => {
      const positionId = user.positionId;
      const positionName = user.role; // Usar el role como nombre de la posición

      if (!positionsMap.has(positionId)) {
        positionsMap.set(positionId, {
          id: positionId,
          name: positionName,
          users: [],
        });
      }

      positionsMap.get(positionId)!.users.push(user);
    });

    // Convertir el mapa a array y agregar userCount
    const dynamicPositions = Array.from(positionsMap.values()).map(
      (position) => ({
        ...position,
        userCount: position.users.length,
      }),
    );

    // Si no hay posiciones dinámicas, mantener las mock vacías
    return dynamicPositions.length > 0 ? dynamicPositions : mockWorkPositions;
  }, [apiUsers]);

  type CorruptedSaved = {
    id: { primary?: Signer; backup?: Signer };
    name?: string;
    email?: string;
    role?: string;
    positionId?: { primary?: Signer; backup?: Signer } | string;
  };

  // Función para reconstruir firmantes desde IDs guardados
  const reconstructSignersFromIds = useCallback(
    (savedSigners: CorruptedSaved[]) => {
      if (!apiUsers.length || !savedSigners.length) return [];

      return savedSigners.map((savedSigner) => ({
        primary: savedSigner.id?.primary || null,
        backup: savedSigner.id?.backup || null,
      })) as SignersSelectedType;
    },
    [apiUsers],
  );

  const looksLikePlaceholderName = (name: string): boolean =>
    name.includes("-") || name.startsWith("Firmante ");

  const isRealEmail = (email: string): boolean =>
    email.includes("@") && !email.includes("ejemplo.com");

  const isImprovement = useCallback(
    (oldS: Signer | null, newS: Signer | null): newS is Signer => {
      if (!newS) return false; // no hay nada nuevo que mejore
      if (!oldS) return true; // pasar de null a algo es mejora

      const sameId = oldS.id === newS.id;

      const nameImproved =
        looksLikePlaceholderName(oldS.name) &&
        newS.name.length > 0 &&
        !looksLikePlaceholderName(newS.name);

      const emailImproved = !isRealEmail(oldS.email) && isRealEmail(newS.email);

      return sameId && (nameImproved || emailImproved);
    },
    [],
  );

  useEffect(() => {
    if (apiUsers.length === 0 || selectedSigners.length === 0) return;

    const reconstructed = reconstructSignersFromIds(
      selectedSigners as unknown as CorruptedSaved[],
    );

    reconstructed.forEach((recPair, i) => {
      const origPair = selectedSigners[i] ?? { primary: null, backup: null };

      if (isImprovement(origPair.primary, recPair.primary)) {
        onUpdateSigner(i, recPair.primary, "primary");
      }
      if (isImprovement(origPair.backup, recPair.backup)) {
        onUpdateSigner(i, recPair.backup, "backup");
      }
    });
  }, [
    apiUsers,
    selectedSigners,
    onUpdateSigner,
    reconstructSignersFromIds,
    isImprovement,
  ]);

  // Combinar estados de carga y error
  const combinedIsLoading = isLoadingSigners || isLoadingUsers;
  const combinedError =
    signersError ||
    (usersError ? "Error al cargar usuarios del sistema" : null);

  // Convertir la lista plana de firmantes en secciones de firmante principal + suplente
  const sections: SignerSection[] = Array.from(
    { length: numberOfSections },
    (_, i) => ({
      id: `section-${i}`,
      primarySigner: selectedSigners[i]?.primary ?? null,
      backupSigner: selectedSigners[i]?.backup ?? null,
    }),
  );

  const canAddMoreSections = numberOfSections < 4; // Máximo 4 secciones (8 firmantes)

  // Verificar si la última sección está completa (ambos campos llenos)
  const isLastSectionComplete = () => {
    const lastSection = sections[sections.length - 1];
    return lastSection?.primarySigner;
  };

  // Filtrar firmantes disponibles para un select específico
  const getAvailableSignersForSelect = () => {
    const allUsers = workPositionsWithApiUsers.flatMap(
      (position) => position.users,
    );

    // IDs ya usados
    const selectedIds = selectedSigners
      .flatMap((pair) => [pair.primary?.id, pair.backup?.id])
      .filter((id): id is string => Boolean(id));

    return allUsers.filter((user) => !selectedIds.includes(user.id));
  };

  type SignerPair = { primary: Signer | null; backup: Signer | null };

  const availableByPosition = useMemo<WorkPosition[]>(() => {
    // mover esta función dentro del useMemo
    const getSelectedIdsSet = (pairs: SignerPair[]) =>
      new Set(
        pairs
          .flatMap((p) => [p.primary?.id, p.backup?.id])
          .filter((id): id is string => Boolean(id)),
      );

    const taken = getSelectedIdsSet(selectedSigners);

    return workPositionsWithApiUsers
      .map((pos) => ({
        ...pos,
        users: pos.users.filter((u) => !taken.has(u.id)),
      }))
      .filter((pos) => pos.users.length > 0);
  }, [selectedSigners, workPositionsWithApiUsers]);

  const handlePrimarySignerChange = (
    sectionIndex: number,
    signerId: string,
  ) => {
    if (signerId === "no-signers") {
      onAddSigner(undefined, "primary", sectionIndex);
      return;
    }

    // Buscar el usuario en todas las posiciones de trabajo
    let signer: Signer | undefined;
    for (const position of workPositionsWithApiUsers) {
      signer = position.users.find((user) => user.id === signerId);
      if (signer) break;
    }

    if (signer) {
      const flatIndex = sectionIndex;

      if (flatIndex < selectedSigners.length && selectedSigners[flatIndex]) {
        onUpdateSigner(flatIndex, signer, "primary");
      } else {
        onAddSigner(signer, "primary", flatIndex);
      }
    }
  };

  const handleBackupSignerChange = (sectionIndex: number, signerId: string) => {
    if (signerId === "no-signers") {
      onAddSigner(undefined, "backup", sectionIndex);
      return;
    }

    let signer: Signer | undefined;
    for (const position of workPositionsWithApiUsers) {
      signer = position.users.find((user) => user.id === signerId);
      if (signer) break;
    }

    if (signer) {
      const flatIndex = sectionIndex;

      if (selectedSigners[flatIndex]) {
        onUpdateSigner(flatIndex, signer, "backup");
      } else {
        onAddSigner(signer, "backup", flatIndex);
      }
    }
  };

  const handleAddSection = () => {
    if (canAddMoreSections) {
      setNumberOfSections((prev) => prev + 1);
      handlePrimarySignerChange(numberOfSections, "no-signers");
    }
  };

  const handleRemoveSection = (sectionIndex: number) => {
    if (sectionIndex === 0) return; // No permitir eliminar la primera sección

    const section = sections[sectionIndex];
    if (section.primarySigner) {
      onRemoveSigner(section.primarySigner.id);
    }
    if (section.backupSigner) {
      onRemoveSigner(section.backupSigner.id);
    }
    if (!section.backupSigner && !section.primarySigner) {
      onRemoveSigner();
    }

    setNumberOfSections((prev) => Math.max(1, prev - 1));
  };

  return (
    <div className="w-full pt-3 pb-6 px-4 flex flex-col gap-6">
      {sections.map((section, sectionIndex) => {
        const sectionLabel =
          sectionIndex === 0
            ? "Firmante y suplente"
            : `Firmante y suplente ${sectionIndex + 1}`;

        const isRequired = true; // Todas las secciones son requeridas

        return (
          <div key={section.id} className="relative">
            {/* Header de la sección */}
            <div className="flex items-center justify-between mb-3">
              <label className="font-semibold text-body-loud leading-l">
                {sectionLabel}
                {isRequired && <span className="text-danger ml-1">*</span>}
              </label>
            </div>

            {/* Campos de la sección */}
            <div className="flex items-center gap-2">
              <div className="flex flex-col gap-0 flex-1">
                <div className="flex h-11">
                  {/* Primer select - Firmante principal */}
                  <PrimarySignerSelector
                    availableSigners={getAvailableSignersForSelect()}
                    workPositions={availableByPosition}
                    selectedSigner={section.primarySigner || undefined}
                    onSignerChange={(signer) =>
                      handlePrimarySignerChange(sectionIndex, signer?.id || "")
                    }
                    isLoading={combinedIsLoading}
                    error={combinedError}
                    placeholder="Elige un firmante..."
                    selectedSignerIds={selectedSigners
                      .filter((s) => s)
                      .map((s) => s.primary?.id as string)}
                  />
                </div>

                <div className="flex h-11">
                  {/* Segundo select - Firmante suplente */}
                  <BackupSignerSelector
                    availableSigners={getAvailableSignersForSelect()}
                    workPositions={availableByPosition}
                    selectedSigner={section.backupSigner || undefined}
                    onSignerChange={(signer) =>
                      handleBackupSignerChange(sectionIndex, signer?.id || "")
                    }
                    isLoading={combinedIsLoading}
                    error={combinedError}
                    placeholder="Elige un suplente (opcional)..."
                    selectedSignerIds={selectedSigners
                      .filter((s) => s)
                      .map((s) => s.backup?.id as string)}
                  />
                </div>
              </div>

              {/* Botón eliminar - centrado verticalmente */}
              {sectionIndex > 0 && (
                <div className="flex items-center h-22">
                  <Button
                    variant="quiet"
                    size="sm"
                    onClick={() => handleRemoveSection(sectionIndex)}
                    className="font-semibold inline-flex items-center justify-center whitespace-nowrap transition-all disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive cursor-pointer border-transparent bg-transparent text-body-loud disabled:text-on-disabled size-8 text-sm leading-2xl border rounded-lg hover:border-neutral-quiet focus:bg-neutral-object-accent-silent focus:border-neutral-quiet"
                    aria-label="Eliminar"
                  >
                    <Icon type="far" name="times" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Botón para agregar nueva sección */}
      {canAddMoreSections && (
        <div className="mt-3 flex items-center">
          <div className="flex-1 border-t border-neutral-quiet"></div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleAddSection}
            disabled={
              combinedIsLoading || !!combinedError || !isLastSectionComplete()
            }
            className="mx-4 flex items-center justify-center gap-2 text-body-quiet py-2 px-3"
          >
            Añadir firmante
            <Icon type="far" name="plus" />
          </Button>
          <div className="flex-1 border-t border-neutral-quiet"></div>
        </div>
      )}
    </div>
  );
};

// Componente para seleccionar firmante principal
const PrimarySignerSelector = ({
  workPositions,
  selectedSigner,
  onSignerChange,
  isLoading,
  error,
  placeholder,
  selectedSignerIds,
}: {
  availableSigners: Signer[];
  workPositions: WorkPosition[];
  selectedSigner?: Signer;
  onSignerChange: (signer: Signer | null) => void;
  isLoading: boolean;
  error: string | null;
  placeholder: string;
  selectedSignerIds: string[];
}) => {
  const [open, setOpen] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState<WorkPosition | null>(
    null,
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [searchUserQuery, setSearchUserQuery] = useState("");

  // Filtrar posiciones por búsqueda y usuarios disponibles
  const filteredPositions = workPositions
    .filter((position) =>
      position.name.toLowerCase().includes(searchQuery.toLowerCase()),
    )
    .map((position) => ({
      ...position,
      users: position.users.filter(
        (user) =>
          !selectedSignerIds.includes(user.id) ||
          user.id === selectedSigner?.id,
      ),
      userCount: position.users.filter(
        (user) =>
          !selectedSignerIds.includes(user.id) ||
          user.id === selectedSigner?.id,
      ).length,
    }))
    .filter((position) => position.userCount > 0);

  const handlePositionClick = (position: WorkPosition) => {
    if (position.userCount === 1) {
      // Si solo hay un usuario, seleccionar automáticamente
      onSignerChange(position.users[0]);
      setOpen(false);
      setSelectedPosition(null);
    } else {
      // Si hay múltiples usuarios, mostrar submenú
      setSelectedPosition(position);
    }
  };

  const handleUserSelect = (user: Signer) => {
    onSignerChange(user);
    setOpen(false);
    setSelectedPosition(null);
  };

  const handleBackToPositions = () => {
    setSelectedPosition(null);
  };

  if (isLoading) {
    return (
      <Button
        disabled
        className="flex justify-between items-center group gap-3 border-neutral text-body-loud placeholder:text-body-quiet focus-visible:outline-none focus-visible:outline-2 focus-visible:border-contextual-action focus-visible:caret-color-contextual-action disabled:bg-surface disabled:text-on-disabled disabled:cursor-not-allowed px-3 py-2 h-11 text-base leading-3xl border rounded-lg not-disabled:hover:border-neutral-loud focus-visible:hover:border-contextual-action min-w-0 flex-1 rounded-tl-lg rounded-tr-lg rounded-bl-none rounded-br-none border-b-0 focus:z-10 bg-neutral-object hover:bg-neutral-object-accent-silent"
      >
        <div className="w-full flex gap-3 items-center">
          <Icon
            type="far"
            name="spinner"
            className="text-body-subtle animate-spin"
          />
          <span className="text-body-subtle">Cargando firmantes...</span>
        </div>
      </Button>
    );
  }

  if (error) {
    return (
      <Button
        disabled
        className="flex justify-between items-center group gap-3 border-neutral text-body-loud placeholder:text-body-quiet focus-visible:outline-none focus-visible:outline-2 focus-visible:border-contextual-action focus-visible:caret-color-contextual-action disabled:bg-surface disabled:text-on-disabled disabled:cursor-not-allowed px-3 py-2 h-11 text-base leading-3xl border rounded-lg not-disabled:hover:border-neutral-loud focus-visible:hover:border-contextual-action min-w-0 flex-1 rounded-tl-lg rounded-tr-lg rounded-bl-none rounded-br-none border-b-0 focus:z-10 bg-neutral-object hover:bg-neutral-object-accent-silent"
      >
        <div className="w-full flex gap-3 items-center">
          <Icon
            type="far"
            name="exclamation-triangle"
            className="text-danger"
          />
          <span className="text-danger">Error al cargar firmantes</span>
        </div>
      </Button>
    );
  }

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (!isOpen) {
          setSelectedPosition(null);
          setSearchQuery("");
          setSearchUserQuery("");
        }
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          variant="tertiary"
          className={cn(
            "flex justify-start items-center group min-w-0 relative gap-2",
            inputVariants({
              className: cn(
                "bg-neutral-object hover:bg-neutral-object-accent-silent data-[state=open]:bg-neutral-object-accent-quiet data-[state=open]:border-neutral-loud",
                "w-full rounded-tl-lg rounded-tr-lg rounded-bl-none rounded-br-none border-b-0 h-11",
              ),
            }),
          )}
        >
          <div className="w-full flex gap-3 items-center">
            {selectedSigner ? (
              <div className="w-full flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-surface-action-accent-quiet border border-contextual-action flex items-center justify-center text-xs font-semibold text-body">
                  {getInitials(selectedSigner.name)}
                </div>
                <div className="flex gap-2 items-center flex-auto">
                  <span className="text-body">{selectedSigner.name}</span>
                </div>
              </div>
            ) : (
              <span className="text-body-subtle">{placeholder}</span>
            )}
          </div>
          <Icon
            type="far"
            name="angle-down"
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-body group-data-[state=open]:rotate-180 transition-transform"
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className={cn(
          "flex flex-col rounded-lg border-[0.5px] border-neutral bg-neutral-object text-body-loud font-semibold shadow-neutral-xl w-[var(--radix-dropdown-menu-trigger-width)]",
        )}
        side="bottom"
        align="start"
      >
        {/* Campo de búsqueda */}
        <div className="border-b border-neutral-quiet p-3">
          {!selectedPosition ? (
            <div className="relative">
              <Icon
                type="far"
                name="search"
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-body"
              />
              <Input
                variant="sm"
                className="text-sm w-full font-normal flex-auto pl-9"
                placeholder="Buscar posición..."
                onChange={(e) => setSearchQuery(e.target.value)}
                value={searchQuery}
              />
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBackToPositions}
                className="p-1 shrink-0"
              >
                <Icon type="fas" name="chevron-left" className="text-body" />
              </Button>
              <div className="relative flex-1">
                <Icon
                  type="far"
                  name="search"
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-body"
                />
                <Input
                  variant="sm"
                  className="text-sm w-full font-normal flex-auto pl-9"
                  placeholder="Buscar..."
                  onChange={(e) => setSearchUserQuery(e.target.value)}
                  value={searchUserQuery}
                />
              </div>
            </div>
          )}
        </div>

        <ScrollArea className="h-[264px] p-2">
          <div className="flex flex-col gap-2 mb-2">
            {!selectedPosition ? (
              <>
                {/* Vista de posiciones de trabajo */}
                <div className="flex items-center gap-2 flex-auto px-3 py-2">
                  <div className="flex gap-2 items-center text-xs text-body-quiet truncate line-clamp-1 first:font-semibold first:text-body">
                    Posición de trabajo
                  </div>
                  <span className="flex-auto border border-neutral-quiet"></span>
                </div>
                {filteredPositions.map((position) => (
                  <div
                    key={position.id}
                    role="menuitem"
                    className="rounded-lg cursor-pointer text-body relative flex items-center gap-2 py-2 px-3 text-sm outline-hidden select-none hover:bg-neutral-object-accent-silent focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
                    onClick={() => handlePositionClick(position)}
                  >
                    <div className="w-full flex items-center gap-3">
                      <div className="flex gap-2 items-center flex-auto">
                        <span className="text-body font-semibold">
                          {position.name}
                        </span>
                        <span className="text-body-quiet text-sm">
                          {position.userCount}{" "}
                          {position.userCount === 1 ? "usuario" : "usuarios"}
                        </span>
                      </div>
                      {position.userCount > 1 && (
                        <Icon
                          type="fas"
                          name="chevron-right"
                          className="text-body-quiet"
                        />
                      )}
                    </div>
                  </div>
                ))}
                {filteredPositions.length === 0 && (
                  <div className="p-4 text-center text-body-quiet">
                    No hay posiciones disponibles
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Vista de usuarios de la posición seleccionada */}
                <div className="flex items-center gap-2 flex-auto px-3 py-2">
                  <div className="flex gap-2 items-center text-xs text-body-quiet truncate line-clamp-1 first:font-semibold first:text-body">
                    {selectedPosition.name}
                  </div>
                  <span className="flex-auto border border-neutral-quiet"></span>
                </div>
                {selectedPosition.users
                  .filter(({ name }) => {
                    if (!searchUserQuery) return true;
                    return name
                      .toLowerCase()
                      .includes(searchUserQuery.toLowerCase());
                  })
                  .map((user) => (
                    <div
                      key={user.id}
                      role="menuitem"
                      className="rounded-lg cursor-pointer text-body relative flex items-center gap-2 py-2 px-3 text-sm outline-hidden select-none hover:bg-neutral-object-accent-silent focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
                      onClick={() => handleUserSelect(user)}
                    >
                      <div className="w-full flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full bg-surface-action-accent-quiet border border-contextual-action flex items-center justify-center text-xs font-semibold text-body">
                          {getInitials(user.name)}
                        </div>
                        <div className="flex flex-col flex-auto">
                          <span className="font-semibold text-body">
                            {user.name}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
              </>
            )}
          </div>
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// Componente para seleccionar firmante suplente
const BackupSignerSelector = ({
  workPositions,
  selectedSigner,
  onSignerChange,
  isLoading,
  error,
  placeholder,
  selectedSignerIds,
}: {
  availableSigners: Signer[];
  workPositions: WorkPosition[];
  selectedSigner?: Signer;
  onSignerChange: (signer: Signer | null) => void;
  isLoading: boolean;
  error: string | null;
  placeholder: string;
  selectedSignerIds: string[];
}) => {
  const [open, setOpen] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState<WorkPosition | null>(
    null,
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [searchUserQuery, setSearchUserQuery] = useState("");

  // Filtrar posiciones por búsqueda y usuarios disponibles
  const filteredPositions = workPositions
    .filter((position) =>
      position.name.toLowerCase().includes(searchQuery.toLowerCase()),
    )
    .map((position) => ({
      ...position,
      users: position.users.filter(
        (user) =>
          !selectedSignerIds.includes(user.id) ||
          user.id === selectedSigner?.id,
      ),
      userCount: position.users.filter(
        (user) =>
          !selectedSignerIds.includes(user.id) ||
          user.id === selectedSigner?.id,
      ).length,
    }))
    .filter((position) => position.userCount > 0);

  const handlePositionClick = (position: WorkPosition) => {
    if (position.userCount === 1) {
      // Si solo hay un usuario, seleccionar automáticamente
      onSignerChange(position.users[0]);
      setOpen(false);
      setSelectedPosition(null);
    } else {
      // Si hay múltiples usuarios, mostrar submenú
      setSelectedPosition(position);
    }
  };

  const handleUserSelect = (user: Signer) => {
    onSignerChange(user);
    setOpen(false);
    setSelectedPosition(null);
  };

  const handleBackToPositions = () => {
    setSelectedPosition(null);
  };

  if (isLoading) {
    return (
      <Button
        disabled
        className="flex justify-between items-center group gap-3 border-neutral text-body-loud placeholder:text-body-quiet focus-visible:outline-none focus-visible:outline-2 focus-visible:border-contextual-action focus-visible:caret-color-contextual-action disabled:bg-surface disabled:text-on-disabled disabled:cursor-not-allowed px-3 py-2 h-11 text-base leading-3xl border rounded-lg not-disabled:hover:border-neutral-loud focus-visible:hover:border-contextual-action min-w-0 flex-1 rounded-tl-none rounded-tr-none rounded-bl-lg rounded-br-lg border-t focus:z-10 bg-neutral-object hover:bg-neutral-object-accent-silent"
      >
        <div className="w-full flex gap-3 items-center">
          <Icon
            type="far"
            name="spinner"
            className="text-body-subtle animate-spin"
          />
          <span className="text-body-subtle">Cargando firmantes...</span>
        </div>
      </Button>
    );
  }

  if (error) {
    return (
      <Button
        disabled
        className="flex justify-between items-center group gap-3 border-neutral text-body-loud placeholder:text-body-quiet focus-visible:outline-none focus-visible:outline-2 focus-visible:border-contextual-action focus-visible:caret-color-contextual-action disabled:bg-surface disabled:text-on-disabled disabled:cursor-not-allowed px-3 py-2 h-11 text-base leading-3xl border rounded-lg not-disabled:hover:border-neutral-loud focus-visible:hover:border-contextual-action min-w-0 flex-1 rounded-tl-none rounded-tr-none rounded-bl-lg rounded-br-lg border-t focus:z-10 bg-neutral-object hover:bg-neutral-object-accent-silent"
      >
        <div className="w-full flex gap-3 items-center">
          <Icon
            type="far"
            name="exclamation-triangle"
            className="text-danger"
          />
          <span className="text-danger">Error al cargar firmantes</span>
        </div>
      </Button>
    );
  }

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (!isOpen) {
          setSelectedPosition(null);
          setSearchQuery("");
          setSearchUserQuery("");
        }
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          variant="tertiary"
          className={cn(
            "flex justify-start items-center group min-w-0 relative gap-2",
            inputVariants({
              className: cn(
                "bg-neutral-object hover:bg-neutral-object-accent-silent data-[state=open]:bg-neutral-object-accent-quiet data-[state=open]:border-neutral-loud",
                "w-full rounded-tl-none rounded-tr-none rounded-bl-lg rounded-br-lg border-t h-11",
              ),
            }),
          )}
        >
          <div className="w-full flex gap-3 items-center">
            {selectedSigner ? (
              <div className="w-full flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-surface-action-accent-quiet border border-contextual-action flex items-center justify-center text-xs font-semibold text-body">
                  {getInitials(selectedSigner.name)}
                </div>
                <div className="flex gap-2 items-center flex-auto">
                  <span className="text-body">{selectedSigner.name}</span>
                </div>
              </div>
            ) : (
              <span className="text-body-subtle">{placeholder}</span>
            )}
          </div>
          <Icon
            type="far"
            name="angle-down"
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-body group-data-[state=open]:rotate-180 transition-transform"
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className={cn(
          "flex flex-col rounded-lg border-[0.5px] border-neutral bg-neutral-object text-body-loud font-semibold shadow-neutral-xl w-[var(--radix-dropdown-menu-trigger-width)]",
        )}
        side="bottom"
        align="start"
      >
        {/* Campo de búsqueda */}
        <div className="border-b border-neutral-quiet p-3">
          {!selectedPosition ? (
            <div className="relative">
              <Icon
                type="far"
                name="search"
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-body"
              />
              <Input
                variant="sm"
                className="text-sm w-full font-normal flex-auto pl-9"
                placeholder="Buscar posición..."
                onChange={(e) => setSearchQuery(e.target.value)}
                value={searchQuery}
              />
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBackToPositions}
                className="p-1 shrink-0"
              >
                <Icon type="fas" name="chevron-left" className="text-body" />
              </Button>
              <div className="relative flex-1">
                <Icon
                  type="far"
                  name="search"
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-body"
                />
                <Input
                  variant="sm"
                  className="text-sm w-full font-normal flex-auto pl-9"
                  placeholder="Buscar..."
                  onChange={(e) => setSearchUserQuery(e.target.value)}
                  value={searchUserQuery}
                />
              </div>
            </div>
          )}
        </div>

        <ScrollArea className="h-[264px] p-2">
          <div className="flex flex-col gap-2 mb-2">
            {!selectedPosition ? (
              <>
                {/* Vista de posiciones de trabajo */}
                <div className="flex items-center gap-2 flex-auto px-3 py-2">
                  <div className="flex gap-2 items-center text-xs text-body-quiet truncate line-clamp-1 first:font-semibold first:text-body">
                    Posición de trabajo
                  </div>
                  <span className="flex-auto border border-neutral-quiet"></span>
                </div>
                {filteredPositions.map((position) => (
                  <div
                    key={position.id}
                    role="menuitem"
                    className="rounded-lg cursor-pointer text-body relative flex items-center gap-2 py-2 px-3 text-sm outline-hidden select-none hover:bg-neutral-object-accent-silent focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
                    onClick={() => handlePositionClick(position)}
                  >
                    <div className="w-full flex items-center gap-3">
                      <div className="flex gap-2 items-center flex-auto">
                        <span className="text-body font-semibold">
                          {position.name}
                        </span>
                        <span className="text-body-quiet text-sm">
                          {position.userCount}{" "}
                          {position.userCount === 1 ? "usuario" : "usuarios"}
                        </span>
                      </div>
                      {position.userCount > 1 && (
                        <Icon
                          type="fas"
                          name="chevron-right"
                          className="text-body-quiet"
                        />
                      )}
                    </div>
                  </div>
                ))}
                {filteredPositions.length === 0 && (
                  <div className="p-4 text-center text-body-quiet">
                    No hay posiciones disponibles
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Vista de usuarios de la posición seleccionada */}
                <div className="flex items-center gap-2 flex-auto px-3 py-2">
                  <div className="flex gap-2 items-center text-xs text-body-quiet truncate line-clamp-1 first:font-semibold first:text-body">
                    {selectedPosition.name}
                  </div>
                  <span className="flex-auto border border-neutral-quiet"></span>
                </div>
                {selectedPosition.users
                  .filter(({ name }) => {
                    if (!searchUserQuery) return true;
                    return name
                      .toLowerCase()
                      .includes(searchUserQuery.toLowerCase());
                  })
                  .map((user) => (
                    <div
                      key={user.id}
                      role="menuitem"
                      className="rounded-lg cursor-pointer text-body relative flex items-center gap-2 py-2 px-3 text-sm outline-hidden select-none hover:bg-neutral-object-accent-silent focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
                      onClick={() => handleUserSelect(user)}
                    >
                      <div className="w-full flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full bg-surface-action-accent-quiet border border-contextual-action flex items-center justify-center text-xs font-semibold text-body">
                          {getInitials(user.name)}
                        </div>
                        <div className="flex flex-col flex-auto">
                          <span className="font-semibold text-body">
                            {user.name}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
              </>
            )}
          </div>
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default SignersList;
